import { Text, styled } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { dpsSend } from 'src/bff';
import { CarlineSelect, Email, PantallaNombreApellido, Telefono, ContactInfoWrapper } from 'src/components';
import { FormController, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { FormDataGroup, FormInfo, IdLead, IdPerson, OneFormState, Steps } from 'src/types';
import { useInitializeLEMForm } from '.';
import { formDataBuilder } from '../format-fields';
import { LEMFormProps } from './LEMController';
import { checkIdLeadHasCarline } from './use-initialize-lem-form';
import { LEMLastScreenFields, ThanKYouLEM } from 'src/forms/LEM';

export enum LEMNoContactadoSteps {
  Modelo,
  NombreApellido,
  EmailTelefono,
  EnviarInformacion,
}

const getEmailTelefonoTitle = (name: string) => {
  return (
    <>
      {name}, ¿nos podrías confirmar tus <Text bold>datos de contacto?</Text>
    </>
  );
};

export function LEMNoContactadoForm(props: LEMFormProps) {
  const { dpsData, idLead, idPerson, handleError, presubmit, trackFormLoad } = props;
  const [loadingPresubmit, setLoadingPresubmit] = useState(true);
  const { initializeForm } = useOneFormContext();
  const store = useStore();
  const { name } = useSelector((state: OneFormState) => state.formData.fields);
  const dispatch = useDispatch();
  const stepsWithModel: Steps[] = [
    {
      title: (
        <>
          <Text bold>¿Por qué modelo </Text>contactaste con tu concesionario?
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <div style={{ marginBottom: '2px' }}>
              <CarlineSelect useCarline={true} notALayer={true} />
            </div>
          }
        />
      ),
      screenIndex: LEMNoContactadoSteps.Modelo,
      name: 'Modelo',
      outputs: ['modelo'],
      outputsText: ['modelo'],
    },
    {
      title: (
        <>
          Para averiguar qué ha pasado, necesitamos saber <Text bold>quién eres</Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <div style={{ marginBottom: '2px' }}>
              <PantallaNombreApellido notALayer={true} />
            </div>
          }
        />
      ),
      screenIndex: LEMNoContactadoSteps.NombreApellido,
      name: 'NombreApellido',
      outputs: ['name', 'surname', 'secondSurname'],
      outputsText: ['name', 'surname', 'secondSurname'],
    },
    {
      title: getEmailTelefonoTitle(idPerson.FirstName),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <ContactInfoWrapper>
              <Telefono paginaConfirmacion={false} required={true} notALayer={true} />
              <Email paginaConfirmacion={false} required={true} notALayer={true} paddingTop='24px'/>
            </ContactInfoWrapper>
          }
        />
      ),
      screenIndex: LEMNoContactadoSteps.EmailTelefono,
      name: 'EmailTelefono',
      outputs: ['cellphone', 'email'],
      outputsText: ['cellphone', '/', 'email'],
    },
    {
      title: (
        <>
          <Text bold>¿Podemos compartir tu respuesta</Text> con el{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            concesionario?
            <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </>
      ),
      fields: <LEMLastScreenFields />,
      screenIndex: LEMNoContactadoSteps.EnviarInformacion,
      name: 'EnviarInformacion',
      outputs: ['enviarInformacion'],
      outputsText: ['enviarInformacion'],
    },
  ];

  const LEMNoContactadoData: Partial<FormDataGroup> = {
    headers: {},
    fields: {
      name: idPerson.FirstName,
      surname: idPerson.LastName,
      secondSurname: idPerson.TXT_2_Surname__c,
      email: idPerson.TXT_Email__c,
      cellphone: idPerson.NUM_Phone_mobile__c,
      formName: 'lem-no_contactado',
      formType: 'otherform',
      pageCategory: 'Más Información',
    }, // Analítica
  };

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        handleError();
      }
      setLoadingPresubmit(false);
    };
    if (presubmit) {
      sendPresubmit();
    } else {
      setLoadingPresubmit(false);
    }
  }, []);

  useEffect(() => {
    if (name && name !== idPerson.FirstName) {
      const newSteps = [...steps];
      const indexOfEmailTelefono = newSteps.findIndex((step) => step.name === 'EmailTelefono');

      newSteps[indexOfEmailTelefono].title = getEmailTelefonoTitle(name);

      dispatch({ type: 'UPDATE_STEPS', payload: newSteps });
    }
  }, [name]);

  const getCarlineFields = (fields: any, idLead: IdLead) => {
    if (checkIdLeadHasCarline(idLead)) {
      return {
        'fields[COD_INTEREST_CARLINE]': idLead.NUM_COD_INTEREST_CARLINE__c, // SOLO EN SUBMIT
        'fields[DESC_INTEREST_CARLINE]': idLead.DESC_Model__c, // SOLO EN SUBMIT
      };
    } else {
      return {
        'fields[COD_INTEREST_CARLINE]': fields.carlineCode, // SOLO EN SUBMIT
        'fields[DESC_INTEREST_CARLINE]': fields.modelo, // SOLO EN SUBMIT
      };
    }
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { tc, today, url, device } = getParamsForm();

    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_VN_VO]': 'VN',

      'headers[DESC_TOKEN]': 'VW_DDB_FRM_LEM_ENCUESTA_CONTESTADO', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_ID_CLIMAPRO]': idLead.ID_Person_DIM_IMP__c,
      'headers[DESC_ID_CAMPAIGN]': '7010Y0000001tAOQAY',
      'headers[DESC_FORM_DETAIL]': 'LEM No Contactado', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORMOBJECTIVE]': 'Encuesta',
      'headers[DESC_DEVICETYPE]': device,
      'headers[DESC_CAMPAIGNBLOCK]': '9. VW LEM',
      'headers[DESC_BRAND]': 'VW',
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,

      'fields[NUM_CELLPHONE]': idPerson.NUM_Phone_mobile__c,
      'fields[DESC_SURNAME_2]': idPerson.TXT_2_Surname__c,
      'fields[DESC_SURNAME_1]': idPerson.LastName,
      'fields[DESC_NAME]': idPerson.FirstName,
      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      'fields[DESC_EMAIL_ADDRESS]': idPerson.TXT_Email__c,
      'fields[DESC_DEALER_SHARE]': fields.enviarInformacion === 'Sí' ? 'True' : 'False', // SOLO EN SUBMIT
      'fields[DESC_DEALER_CODE]': idLead.TXT_DESC_DEALER_CODE__c,
      'fields[DESC_DEALER]': idLead.DESC_Dealer_Code__c,
      'fields[COD_LEAD_ID]': idLead.Id,
      ...getCarlineFields(fields, idLead),

      'flags[FLAG_ISDATA_CHANGED]': checkIfDataChanged(fields, idPerson), // Hay que detectar si el usuario cambia algún dato
      'flags[FLAG_CONTACTED]': dpsData.contactado,
    };
  };

  const checkIfDataChanged = (fields: any, idPerson: IdPerson) => {
    if (
      fields.name === idPerson.FirstName &&
      fields.surname === idPerson.LastName &&
      fields.secondSurname === idPerson.TXT_2_Surname__c &&
      fields.email === idPerson.TXT_Email__c &&
      fields.cellphone === idPerson.NUM_Phone_mobile__c
    ) {
      return 'false';
    }
    return 'true';
  };

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(LEMNoContactadoData);

    delete data['fields[DESC_DEALER_SHARE]'];
    delete data['fields[DESC_NAME]'];
    delete data['fields[DESC_SURNAME_1]'];
    delete data['fields[DESC_SURNAME_2]'];
    delete data['fields[DESC_EMAIL_ADDRESS]'];
    delete data['fields[NUM_CELLPHONE]'];
    delete data['fields[COD_INTEREST_CARLINE]'];
    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_INTEREST_CARLINE]'];
    delete data['headers[PINCODE]'];

    data['headers[DESC_FORM_DETAIL]'] = 'LEM Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FRM_LEM_ENCUESTA';
    data['flags[FLAG_ISDATA_CHANGED]'] = 'false';

    return data;
  };
  const generateSubmitData = () => generateData();

  const sendForm = (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();

      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);
      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response.data?.content?.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { loading, steps, firstStep, lastStep } = useInitializeLEMForm(stepsWithModel, idLead);

  useEffect(() => {
    const LEMNoContactadoInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThanKYouLEM />,
    };
    if (!loading) {
      initializeForm(LEMNoContactadoInfo, LEMNoContactadoData, true);
      trackFormLoad();
    }
  }, [loading]);

  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
